import type {
  ArgusWorkflowContextV1,
  ArgusWorkflowId,
  ArgusRunnerMode,
} from '../types';

const WORKFLOW_STORAGE_KEY = 'argus_workflow_context';
const CAPTURE_MODE = String(import.meta.env.VITE_CAPTURE_MODE ?? 'video').toLowerCase();

export type ArgusWorkflowDefinition = ArgusWorkflowContextV1 & {
  shortLabel: string;
  summary: string;
  timingLabel: string;
};

const WORKFLOWS: Record<ArgusWorkflowId, ArgusWorkflowDefinition> = {
  legacy: {
    version: 1,
    workflowId: 'legacy',
    label: '传统上传流程',
    shortLabel: '旧版现场判读',
    summary: '沿用原有 face-landmarks 上传流程，继续输出在线风险结果。',
    timingLabel: '约 7 分钟',
    protocolId: 'argus-legacy-diagnosis-v1',
    configPath: '/gallery-config.yaml',
    runnerMode: 'upload',
    estimatedDurationSeconds: 420,
    liveDiagnosis: true,
    storesFullVideo: false,
    videoBitsPerSecond: null,
  },
  'screening-v2': {
    version: 1,
    workflowId: 'screening-v2',
    label: '认知筛查采集流程',
    shortLabel: '筛查采集',
    summary: '保存高压缩完整视频、时间线与受试者元数据，并给出即时综合筛查摘要。',
    timingLabel: '4 分 34 秒',
    protocolId: 'argus-screening-v2-2-collection',
    configPath: '/gallery-config-screening-v2.yaml',
    runnerMode: 'video',
    estimatedDurationSeconds: 274,
    liveDiagnosis: false,
    storesFullVideo: true,
    videoBitsPerSecond: 750_000,
  },
};

const DEFAULT_WORKFLOW_ID: ArgusWorkflowId = CAPTURE_MODE === 'upload' ? 'legacy' : 'screening-v2';

const isWorkflowId = (value: unknown): value is ArgusWorkflowId =>
  value === 'legacy' || value === 'screening-v2';

const canonicalizeWorkflow = (workflowId: ArgusWorkflowId): ArgusWorkflowDefinition =>
  WORKFLOWS[workflowId];

export const listArgusWorkflows = (): ArgusWorkflowDefinition[] => [
  WORKFLOWS.legacy,
  WORKFLOWS['screening-v2'],
];

export const getDefaultArgusWorkflow = (): ArgusWorkflowDefinition =>
  canonicalizeWorkflow(DEFAULT_WORKFLOW_ID);

export const getArgusWorkflowById = (workflowId?: string | null): ArgusWorkflowDefinition =>
  isWorkflowId(workflowId) ? canonicalizeWorkflow(workflowId) : getDefaultArgusWorkflow();

export const isVideoWorkflow = (runnerMode: ArgusRunnerMode): boolean => runnerMode === 'video';

export const buildArgusRunnerPath = (path: string, workflowId: ArgusWorkflowId): string => {
  const params = new URLSearchParams({ workflow: workflowId });
  return `${path}?${params.toString()}`;
};

export const persistArgusWorkflow = (workflow: ArgusWorkflowContextV1): void => {
  try {
    window.localStorage.setItem(
      WORKFLOW_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        workflowId: workflow.workflowId,
      }),
    );
  } catch {
    // ignore storage failures
  }
};

export const readPersistedArgusWorkflow = (): ArgusWorkflowDefinition => {
  try {
    const raw = window.localStorage.getItem(WORKFLOW_STORAGE_KEY);
    if (!raw) return getDefaultArgusWorkflow();
    const parsed = JSON.parse(raw) as { workflowId?: unknown };
    if (!isWorkflowId(parsed?.workflowId)) return getDefaultArgusWorkflow();
    return canonicalizeWorkflow(parsed.workflowId);
  } catch {
    return getDefaultArgusWorkflow();
  }
};

export const resolveArgusWorkflow = (search = ''): ArgusWorkflowDefinition => {
  try {
    const params = new URLSearchParams(search);
    const workflowId = params.get('workflow');
    return workflowId ? getArgusWorkflowById(workflowId) : readPersistedArgusWorkflow();
  } catch {
    return readPersistedArgusWorkflow();
  }
};
